import { generateMasterKey } from "@taskfortress/core/src/crypto/utils"
import React, { useCallback, useEffect, useState } from "react"
import { Alert, Platform } from "react-native"
import { Box } from "../Box"
import { Button } from "../Button"
import { Icon } from "../Icons"
import { Pressable } from "../Pressable"
import { Text } from "../Text"
import { TextLink } from "../TextLink"
import { useToast } from "../Toast"
import { ToggleField } from "../form"

interface GenerateMasterKeyProps {
  onMasterKeyConfirmed: (masterKey: string) => void
  onBack: () => void
  isLoading?: boolean
  error?: string | null
}

export const GenerateMasterKey: React.FC<GenerateMasterKeyProps> = ({
  onMasterKeyConfirmed,
  onBack,
  isLoading = false,
  error,
}) => {
  const [masterKey, setMasterKey] = useState<string>("")
  const [hasWrittenDown, setHasWrittenDown] = useState(false)
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [isKeyVisible, setIsKeyVisible] = useState(false)
  const { showToast } = useToast()

  // Generate master key on mount
  useEffect(() => {
    try {
      const newMasterKey = generateMasterKey()
      setMasterKey(newMasterKey)
    } catch (err) {
      console.error("Failed to generate master key:", err)
      Alert.alert("Error", "Failed to generate master key. Please try again.")
    }
  }, [])

  const handleRegenerateMasterKey = useCallback(() => {
    try {
      const newMasterKey = generateMasterKey()
      setMasterKey(newMasterKey)
      setHasWrittenDown(false) // Reset confirmation when regenerating
    } catch (err) {
      console.error("Failed to regenerate master key:", err)
      showToast({
        title: "Error: Failed to regenerate master key",
        action: "error",
      })
    }
  }, [showToast])

  const handleCopyToClipboard = useCallback(async () => {
    if (Platform.OS === "web") {
      try {
        await navigator.clipboard.writeText(masterKey)
        showToast({
          title: "Master key has been copied to clipboard",
          action: "success",
        })
      } catch (err) {
        console.error("Failed to copy to clipboard:", err)
        showToast({
          title: "Error: Failed to copy to clipboard",
          action: "error",
        })
      }
    }
  }, [masterKey, showToast])

  const toggleKeyVisibility = useCallback(() => {
    setIsKeyVisible(!isKeyVisible)
  }, [isKeyVisible])

  const handleContinue = useCallback(() => {
    if (!hasWrittenDown) {
      Alert.alert(
        "Please Confirm",
        "You must confirm that you have written down your master key before continuing.",
      )
      return
    }
    setShowConfirmation(true)
  }, [hasWrittenDown])

  const handleFinalConfirmation = useCallback(() => {
    onMasterKeyConfirmed(masterKey)
  }, [masterKey, onMasterKeyConfirmed])

  if (showConfirmation) {
    return (
      <div className="space-y-6">
        <Text className="text-typography-500 mb-6 block text-center text-lg w-7/12 mx-auto">
          Are you absolutely sure you have safely stored your master key? This
          is your last chance to go back and copy it again.
        </Text>

        {error && (
          <div className="bg-error-50 p-3 rounded-lg">
            <Text className="text-error-600 text-center">{error}</Text>
          </div>
        )}

        <Box className="flex flex-col items-center gap-2">
          <Button
            onPress={handleFinalConfirmation}
            disabled={isLoading}
            isLoading={isLoading}
          >
            Yes, I have safely stored my master key
          </Button>

          <TextLink
            onPress={() => setShowConfirmation(false)}
            disabled={isLoading}
            className="text-center"
          >
            Go back to copy again
          </TextLink>
        </Box>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="bg-warning-50 border-2 border-warning-200 p-4 rounded-lg">
        <Text className="block font-mono text-2xl text-center break-all font-bold text-warning-800">
          {isKeyVisible ? masterKey : "•".repeat(masterKey.length)}
        </Text>
      </div>

      {/* Action buttons row */}
      <Box className="flex flex-row justify-center items-center gap-3 w-6/12 mx-auto">
        <Pressable
          onPress={toggleKeyVisibility}
          className="bg-background-100 hover:bg-background-200 p-3 rounded-lg items-center justify-center"
        >
          {isKeyVisible ? (
            <Icon
              name="eye-slash"
              className="w-5 h-5 text-typography-600 mb-1"
            />
          ) : (
            <Icon name="eye" className="w-5 h-5 text-typography-600 mb-1" />
          )}
          <Text className="text-xs text-typography-600">
            {isKeyVisible ? "Hide" : "Show"}
          </Text>
        </Pressable>

        <Pressable
          onPress={handleRegenerateMasterKey}
          className="bg-background-100 hover:bg-background-200 p-3 rounded-lg items-center justify-center"
          disabled={isLoading}
        >
          <Icon
            name="rotate-ccw"
            className="w-5 h-5 text-typography-600 mb-1"
          />
          <Text className="text-xs text-typography-600">Regenerate</Text>
        </Pressable>

        {Platform.OS === "web" && (
          <Pressable
            onPress={handleCopyToClipboard}
            className="bg-background-100 hover:bg-background-200 p-3 rounded-lg items-center justify-center"
            disabled={!masterKey}
          >
            <Icon name="copy" className="w-5 h-5 text-typography-600 mb-1" />
            <Text className="text-xs text-typography-600">Copy</Text>
          </Pressable>
        )}
      </Box>

      <div className="bg-error-50 border border-error-200 p-4 rounded-lg">
        <Text className="text-error-700 font-semibold mb-2 block text-lg">
          ⚠️ Important Security Notice
        </Text>
        <Text className="text-error-600 text-sm leading-relaxed block">
          • Write down this master key and store it in a safe place.
          {"\n"}• Do not store it digitally unless in a secure password manager.
          {"\n"}• If you lose this key OR your password, you will lose access to
          all of your encrypted data.
          {"\n"}• TaskFortress cannot recover your master key OR password if
          either are lost.
        </Text>
      </div>

      <Box className="flex flex-row justify-center items-center w-6/12 mx-auto">
        <ToggleField
          name="hasWrittenDown"
          label="I have written down my master key in a safe place"
          value={hasWrittenDown}
          handleChange={setHasWrittenDown}
          disabled={isLoading}
          className="text-center w-full"
          inline={true}
        />
      </Box>

      {error && (
        <div className="bg-error-50 p-3 rounded-lg">
          <Text className="text-error-600 text-center">{error}</Text>
        </div>
      )}

      <Box className="flex flex-col items-center gap-2">
        <Button
          onPress={handleContinue}
          disabled={isLoading || !hasWrittenDown}
          isLoading={isLoading}
        >
          Continue
        </Button>

        <TextLink onPress={onBack} disabled={isLoading} className="text-center">
          Back
        </TextLink>
      </Box>
    </div>
  )
}
